import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, X, CaretDown, CaretRight, MagnifyingGlass, Wrench, Pencil, Check, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { newScopeId } from "../builderStorage";
import { PRODUCT_MASTER, HOSTING_GROUP_CODE, hydrateProductMasterFromCatalogue } from "../productMasterHierarchy";

function formatMoney(n, currency = "INR") {
    if (typeof n !== "number" || isNaN(n)) return "—";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
}

/* ─────────── Scope-row construction helpers ─────────── */

/**
 * Build a scope row for a Product that has NO modules. The Product itself
 * becomes a single selectable line item.
 */
function scopeRowForProduct(product, category) {
    return {
        id: newScopeId(),
        source: "product_master",
        kind: "service",
        capabilities: [],
        assetClass: "",
        // identifier:
        productMasterCode: product.code,
        productCode: product.code,
        moduleCode: null,
        // grouping for the Hosting subtotal:
        groupCode: product.code,
        groupLabel: product.label,
        // optional category parent (only when product lives inside a category):
        categoryCode: category ? category.code : null,
        categoryLabel: category ? category.label : null,
        // displayed in scope lists across the Builder:
        productName: product.label,
        subtitle: category ? category.label : "",
    };
}

/**
 * Build a scope row for a Module under a Product. The module becomes the
 * selectable line item; the product is referenced as the parent grouping.
 */
function scopeRowForModule(product, module, category) {
    return {
        id: newScopeId(),
        source: "product_master",
        kind: "service",
        capabilities: [],
        assetClass: "",
        // identifier:
        productMasterCode: module.code,
        productCode: product.code,
        moduleCode: module.code,
        // grouping for the Hosting subtotal:
        groupCode: product.code,
        groupLabel: product.label,
        categoryCode: category ? category.code : null,
        categoryLabel: category ? category.label : null,
        // displayed in scope lists across the Builder:
        productName: module.label,
        subtitle: category
            ? `${category.label} · ${product.label}`
            : product.label,
    };
}

/**
 * Section 1 — Product Selection (REEDOS Product Master).
 *
 * Renders the 3-level hierarchy defined in productMasterHierarchy.js:
 *   Business Category → Product → Module
 *
 * UI rules (operator-approved 2026-02-25):
 *   - "category" nodes render as group headers (caret + label, no checkbox).
 *   - "product" nodes render with a checkbox; they show a caret ONLY when
 *     they have modules.
 *   - "module" nodes render as nested checkboxes under their product.
 *   - Parent-checkbox toggles select/deselect all children (product over
 *     its modules; nothing larger because categories aren't selectable).
 *   - Search filters across all categories/products/modules and auto-
 *     expands matches.
 *
 * Backend-untouched: state is held entirely in `scope` / `prototype` and
 * persisted via the builder's localStorage layer.
 */
export default function ProductSelectionSection({ scope, prototype, onChange }) {
    const [search, setSearch] = useState("");
    const [expandedCategories, setExpandedCategories] = useState(() => {
        // First category expanded by default; rest collapsed
        const init = {};
        PRODUCT_MASTER.forEach((node, idx) => {
            if (node.type === "category") init[node.code] = idx === 0;
        });
        return init;
    });
    const [expandedProducts, setExpandedProducts] = useState({});
    const [customName, setCustomName] = useState("");
    const [customKind, setCustomKind] = useState("service");
    const [customDesc, setCustomDesc] = useState("");
    const [showCustomPanel, setShowCustomPanel] = useState(() => (scope || []).some((s) => s.source === "custom"));
    const [editingCustomId, setEditingCustomId] = useState(null);
    const [editingCustomName, setEditingCustomName] = useState("");
    const [editingCustomKind, setEditingCustomKind] = useState("service");
    const [editingCustomDesc, setEditingCustomDesc] = useState("");

    /* ── Index of selected scope rows by the leaf code (product OR module) ── */
    const selectedByCode = useMemo(() => {
        const map = {};
        (scope || []).forEach((s) => {
            const key = s.moduleCode || s.productCode || s.productMasterCode;
            if (key) map[key] = s;
        });
        return map;
    }, [scope]);

    /* ── Custom (non-Master) scope rows — repeatable list with Edit/Remove ── */
    const customRows = useMemo(() => (scope || []).filter((s) => s.source === "custom"), [scope]);

    /* ── Search filter ── */
    const q = search.trim().toLowerCase();
    function matchesQuery(...labels) {
        if (!q) return true;
        const blob = labels.filter(Boolean).join(" ").toLowerCase();
        return blob.includes(q);
    }

    /* ── Build the filtered tree (preserves the original 3-level shape) ── */
    const filteredTree = useMemo(() => {
        if (!q) return PRODUCT_MASTER;
        const result = [];
        PRODUCT_MASTER.forEach((top) => {
            if (top.type === "category") {
                const matchedProducts = [];
                (top.products || []).forEach((p) => {
                    if (matchesQuery(top.label, p.label, p.description)) {
                        matchedProducts.push(p);
                    } else if ((p.modules || []).length > 0) {
                        const mods = p.modules.filter((m) => matchesQuery(top.label, p.label, m.label));
                        if (mods.length > 0) matchedProducts.push({ ...p, modules: mods });
                    }
                });
                if (matchedProducts.length > 0) {
                    result.push({ ...top, products: matchedProducts });
                }
            } else if (top.type === "product") {
                if (matchesQuery(top.label, top.description)) {
                    result.push(top);
                } else if ((top.modules || []).length > 0) {
                    const mods = top.modules.filter((m) => matchesQuery(top.label, m.label));
                    if (mods.length > 0) result.push({ ...top, modules: mods });
                }
            }
        });
        return result;
    }, [q]);

    /* ── Effective expansion state during search auto-expands matches ── */
    const effectiveCategoryExpanded = useMemo(() => {
        if (!q) return expandedCategories;
        const next = { ...expandedCategories };
        filteredTree.forEach((n) => { if (n.type === "category") next[n.code] = true; });
        return next;
    }, [q, filteredTree, expandedCategories]);
    const effectiveProductExpanded = useMemo(() => {
        if (!q) return expandedProducts;
        const next = { ...expandedProducts };
        filteredTree.forEach((top) => {
            if (top.type === "category") {
                (top.products || []).forEach((p) => {
                    if ((p.modules || []).length > 0) next[p.code] = true;
                });
            } else if (top.type === "product" && (top.modules || []).length > 0) {
                next[top.code] = true;
            }
        });
        return next;
    }, [q, filteredTree, expandedProducts]);

    function toggleCategory(code) {
        setExpandedCategories((prev) => ({ ...prev, [code]: !prev[code] }));
    }
    function toggleProduct(code) {
        setExpandedProducts((prev) => ({ ...prev, [code]: !prev[code] }));
    }

    /* ─────────── Selection helpers ─────────── */

    function selectableLeavesOfProduct(product) {
        // For a product WITH modules → modules are leaves.
        // For a product WITHOUT modules → the product itself is the leaf.
        if ((product.modules || []).length > 0) {
            return product.modules.map((m) => m.code);
        }
        return [product.code];
    }

    function setProductChecked(product, category, checked) {
        const hasModules = (product.modules || []).length > 0;
        const isMutex = !!product.mutuallyExclusive && hasModules;
        if (checked) {
            const next = [...(scope || [])];
            if (hasModules) {
                if (isMutex) {
                    // Mutex products: select ONLY the defaultModuleCode (or the first module).
                    const def = product.defaultModuleCode || product.modules[0].code;
                    const target = product.modules.find((m) => m.code === def) || product.modules[0];
                    if (!selectedByCode[target.code]) next.push(scopeRowForModule(product, target, category));
                } else {
                    product.modules.forEach((m) => {
                        if (!selectedByCode[m.code]) next.push(scopeRowForModule(product, m, category));
                    });
                }
            } else {
                if (!selectedByCode[product.code]) next.push(scopeRowForProduct(product, category));
            }
            onChange(next);
        } else {
            const codesToRemove = new Set(selectableLeavesOfProduct(product));
            // For mutex products, also include any module of the product so we clear ALL of them.
            if (isMutex) product.modules.forEach((m) => codesToRemove.add(m.code));
            onChange((scope || []).filter((s) => {
                const key = s.moduleCode || s.productCode || s.productMasterCode;
                return !codesToRemove.has(key);
            }));
        }
    }

    function setModuleChecked(product, module, category, checked) {
        const isMutex = !!product.mutuallyExclusive;
        if (checked) {
            if (selectedByCode[module.code]) return;
            let next = [...(scope || [])];
            if (isMutex) {
                // Mutex: drop every OTHER module of this product before adding the chosen one.
                const otherCodes = new Set(
                    (product.modules || []).filter((m) => m.code !== module.code).map((m) => m.code),
                );
                next = next.filter((s) => !otherCodes.has(s.moduleCode || ""));
            }
            next.push(scopeRowForModule(product, module, category));
            onChange(next);
        } else {
            onChange((scope || []).filter((s) => (s.moduleCode || s.productCode || s.productMasterCode) !== module.code));
        }
    }

    function removeFromScope(scopeId) {
        onChange((scope || []).filter((s) => s.id !== scopeId));
    }

    function addCustomProduct() {
        const name = customName.trim();
        if (!name) {
            toast.error("Custom product needs a name.");
            return;
        }
        const row = {
            id: newScopeId(),
            source: "custom",
            kind: customKind,
            capabilities: [],
            assetClass: "",
            productMasterCode: null,
            productCode: null,
            moduleCode: null,
            groupCode: "custom",
            groupLabel: "Custom",
            categoryCode: null,
            categoryLabel: null,
            productName: name,
            subtitle: "Custom — not from Product Master",
            // Owner directive 2026-09-29 — operator-written product note,
            // rendered under the product name in place of the legacy
            // "One-time" subtitle.
            description: customDesc.trim(),
        };
        onChange([...(scope || []), row]);
        setCustomName("");
        setCustomDesc("");
        toast.success(`Added "${name}" as a custom product.`);
    }

    function startEditCustom(row) {
        setEditingCustomId(row.id);
        setEditingCustomName(row.productName || "");
        setEditingCustomKind(row.kind || "service");
        setEditingCustomDesc(row.description || "");
    }

    function cancelEditCustom() {
        setEditingCustomId(null);
        setEditingCustomName("");
        setEditingCustomKind("service");
        setEditingCustomDesc("");
    }

    function saveEditCustom(id) {
        const name = editingCustomName.trim();
        if (!name) {
            toast.error("Custom product needs a name.");
            return;
        }
        onChange((scope || []).map((s) =>
            s.id === id ? { ...s, productName: name, kind: editingCustomKind, description: editingCustomDesc.trim() } : s
        ));
        cancelEditCustom();
        toast.success("Custom product updated.");
    }

    /* ── Product checkbox tri-state (for products with modules) ── */
    function productCheckboxState(product) {
        const leaves = selectableLeavesOfProduct(product);
        const selected = leaves.filter((code) => selectedByCode[code]).length;
        if (selected === 0) return false;
        // Mutually-exclusive products: any single selection counts as "checked" (never indeterminate)
        if (product.mutuallyExclusive) return true;
        if (selected === leaves.length) return true;
        return "indeterminate";
    }

    /* ── Selected-count helpers (badge displays + summary) ── */
    function selectedCountForProduct(product) {
        return selectableLeavesOfProduct(product).filter((c) => selectedByCode[c]).length;
    }
    function totalLeavesOfProduct(product) {
        return selectableLeavesOfProduct(product).length;
    }
    function selectedCountForCategory(category) {
        let n = 0;
        (category.products || []).forEach((p) => { n += selectedCountForProduct(p); });
        return n;
    }
    function totalLeavesOfCategory(category) {
        let n = 0;
        (category.products || []).forEach((p) => { n += totalLeavesOfProduct(p); });
        return n;
    }

    /* ─────────── Right-rail summary tiles ─────────── */
    const summary = useMemo(() => {
        const cur = prototype?.currency || "INR";
        const items = scope || [];

        // One-Time Charges — Iter 97.2: pricing is now ALWAYS captured at the
        // item level regardless of presentation mode (mode toggles only price
        // visibility in the preview, not the data model). Sum itemPrices.
        const com = prototype?.commercials || {};
        const oneTime = items.reduce((s, x) => s + (Number(com.itemPrices?.[x.id]) || 0), 0);

        // AMC entries indexed by scopeRowId (Iter 92.6 — keys moved to scope-row level)
        const amcByScope = {};
        (prototype?.amc?.items || []).forEach((a) => { amcByScope[a.scopeRowId] = Number(a.amount) || 0; });

        // Hosting subtotal = AMC amounts on rows whose groupCode === "hosting"
        let hosting = 0;
        let amcExHosting = 0;
        items.forEach((s) => {
            const amcVal = amcByScope[s.id] || 0;
            if (s.groupCode === HOSTING_GROUP_CODE) hosting += amcVal;
            else amcExHosting += amcVal;
        });
        const annual = amcExHosting + hosting;

        return { cur, count: items.length, oneTime, annual, amc: amcExHosting, hosting };
    }, [scope, prototype]);

    /* ─────────── Render helpers ─────────── */

    function renderProductRow(product, category, depth) {
        const hasModules = (product.modules || []).length > 0;
        const expanded = hasModules && !!effectiveProductExpanded[product.code];
        const state = productCheckboxState(product);
        const selectedCount = selectedCountForProduct(product);
        const totalLeaves = totalLeavesOfProduct(product);

        const paddingLeft = depth === 0 ? "pl-2" : "pl-6";

        return (
            <div
                key={product.code}
                data-testid={`product-master-product-${product.code}`}
                className={`${
                    selectedCount > 0 ? "bg-indigo-50/40" : "hover:bg-slate-50"
                } rounded transition`}
            >
                <div className={`flex items-center gap-2 ${paddingLeft} pr-3 py-1.5`}>
                    {hasModules ? (
                        <button
                            type="button"
                            onClick={() => toggleProduct(product.code)}
                            className="text-slate-500 hover:text-slate-900 flex-shrink-0"
                            aria-label="Toggle product"
                            data-testid={`product-master-product-toggle-${product.code}`}
                        >
                            {expanded ? <CaretDown weight="bold" /> : <CaretRight weight="bold" />}
                        </button>
                    ) : (
                        <span className="w-[14px] flex-shrink-0" />
                    )}
                    <Checkbox
                        checked={state}
                        onCheckedChange={(v) => setProductChecked(product, category, !!v)}
                        data-testid={`product-master-product-checkbox-${product.code}`}
                        aria-label={`Select ${product.label}`}
                    />
                    <button
                        type="button"
                        onClick={() => hasModules ? toggleProduct(product.code) : setProductChecked(product, category, state !== true)}
                        className="flex-1 min-w-0 text-left"
                    >
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm ${selectedCount > 0 ? "font-semibold text-indigo-900" : "text-slate-800"}`}>
                                {product.label}
                            </span>
                            {hasModules && selectedCount > 0 && (
                                <Badge className="bg-indigo-100 text-indigo-900 hover:bg-indigo-100 text-[10px]" data-testid={`product-master-product-count-${product.code}`}>
                                    {selectedCount}/{totalLeaves}
                                </Badge>
                            )}
                        </div>
                    </button>
                </div>

                {expanded && (
                    <ul className="pb-1.5 pl-10 pr-3 grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
                        {(product.modules || []).map((m) => {
                            const checked = !!selectedByCode[m.code];
                            return (
                                <li key={m.code}>
                                    <label
                                        className={`flex items-center gap-2 p-1 rounded text-sm cursor-pointer transition ${
                                            checked ? "bg-indigo-50 text-indigo-900" : "hover:bg-slate-100 text-slate-700"
                                        }`}
                                        data-testid={`product-master-module-${m.code}`}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onCheckedChange={(v) => setModuleChecked(product, m, category, !!v)}
                                            data-testid={`product-master-module-checkbox-${m.code}`}
                                        />
                                        <span className="truncate">{m.label}</span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    }

    function renderCategoryRow(category) {
        const expanded = !!effectiveCategoryExpanded[category.code];
        const selectedCount = selectedCountForCategory(category);
        const totalLeaves = totalLeavesOfCategory(category);

        return (
            <div
                key={category.code}
                data-testid={`product-master-category-${category.code}`}
                className={`border rounded-md overflow-hidden ${
                    selectedCount > 0 ? "border-indigo-300" : "border-slate-200"
                }`}
            >
                <button
                    type="button"
                    onClick={() => toggleCategory(category.code)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 text-left hover:bg-slate-100"
                    data-testid={`product-master-category-toggle-${category.code}`}
                >
                    {expanded ? <CaretDown weight="bold" /> : <CaretRight weight="bold" />}
                    <span className="font-semibold text-sm text-slate-900">{category.label}</span>
                    <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                        Business Category
                    </span>
                    {selectedCount > 0 && (
                        <Badge className="bg-indigo-100 text-indigo-900 hover:bg-indigo-100 text-[10px]" data-testid={`product-master-category-count-${category.code}`}>
                            {selectedCount}/{totalLeaves} selected
                        </Badge>
                    )}
                </button>
                {expanded && (
                    <div className="bg-white border-t border-slate-200 py-1 space-y-0.5">
                        {(category.products || []).map((p) => renderProductRow(p, category, 1))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Card className="border-slate-200" data-testid="section-product-selection">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2"><Package weight="duotone" /> Section 1 — Product Selection</CardTitle>
                        <CardDescription>
                            REEDOS Product Master — Business Category → Product → Module. Single-item products appear as direct checkboxes; only products with modules expand.
                        </CardDescription>
                    </div>
                    {/* Iter 97.18 — Manual refresh affordance. The Builder
                        force-refreshes the Product Master on mount, but if
                        the operator edits /catalogue in another tab they
                        can pull the latest hierarchy without remounting. */}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-1.5 text-[11px] h-7"
                        onClick={async () => {
                            try {
                                const r = await hydrateProductMasterFromCatalogue({ force: true });
                                toast.success(`Product Master refreshed (${r.count ?? PRODUCT_MASTER.length} items)`);
                            } catch (e) {
                                toast.error(`Refresh failed: ${e?.message || "Unknown error"}`);
                            }
                        }}
                        data-testid="product-master-refresh-btn"
                        title="Refetch the Product Master from /catalogue"
                    >
                        <ArrowsClockwise size={12} weight="bold" /> Refresh master
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid gap-5 lg:grid-cols-[1fr_320px] items-start">
                    {/* LEFT — hierarchy + search + custom */}
                    <div className="space-y-3">
                        <div className="relative">
                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search across all categories, products and modules…"
                                className="pl-9"
                                data-testid="product-master-search"
                            />
                        </div>

                        <div className="space-y-2">
                            {filteredTree.map((top) => {
                                if (top.type === "category") return renderCategoryRow(top);
                                if (top.type === "product") {
                                    return (
                                        <div
                                            key={top.code}
                                            className="border border-slate-200 rounded-md overflow-hidden bg-white"
                                            data-testid={`product-master-top-product-${top.code}`}
                                        >
                                            {renderProductRow(top, null, 0)}
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>

                        {/* Custom product (collapsible) — repeatable Add + per-row Edit/Remove */}
                        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50">
                            <button
                                type="button"
                                onClick={() => setShowCustomPanel((s) => !s)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700"
                                data-testid="custom-product-toggle"
                            >
                                {showCustomPanel ? <CaretDown /> : <CaretRight />} <Wrench weight="duotone" /> Add custom product (not in Master)
                                {customRows.length > 0 && (
                                    <Badge className="ml-1 bg-indigo-100 text-indigo-900 hover:bg-indigo-100 text-[10px]" data-testid="custom-product-count">
                                        {customRows.length}
                                    </Badge>
                                )}
                            </button>
                            {showCustomPanel && (
                                <div className="border-t border-slate-200 p-3 space-y-3 bg-white">
                                    {/* Add row */}
                                    <div className="grid gap-2 md:grid-cols-[1fr_140px_auto]">
                                        <Input
                                            placeholder="Custom product / service name"
                                            value={customName}
                                            onChange={(e) => setCustomName(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomProduct(); } }}
                                            data-testid="custom-product-name"
                                        />
                                        <Select value={customKind} onValueChange={setCustomKind}>
                                            <SelectTrigger data-testid="custom-product-kind"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="product">Product</SelectItem>
                                                <SelectItem value="service">Service</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button onClick={addCustomProduct} size="sm" data-testid="custom-product-add">
                                            <Plus className="mr-1" /> Add
                                        </Button>
                                    </div>
                                    {/* Owner directive 2026-09-29 — "About this product" note for
                                        custom rows; renders under the product name on the proposal
                                        in place of the legacy "One-time" subtitle. */}
                                    <Textarea
                                        placeholder="About this product (optional) — shown on the proposal under the product name"
                                        value={customDesc}
                                        onChange={(e) => setCustomDesc(e.target.value)}
                                        maxLength={2000}
                                        rows={5}
                                        className="text-sm resize-y min-h-[110px]"
                                        data-testid="custom-product-desc"
                                    />

                                    {/* Custom products list */}
                                    {customRows.length > 0 && (
                                        <div className="space-y-1.5" data-testid="custom-product-list">
                                            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                                                Custom Products Added
                                            </div>
                                            <ul className="space-y-1">
                                                {customRows.map((row) => {
                                                    const isEditing = editingCustomId === row.id;
                                                    return (
                                                        <li
                                                            key={row.id}
                                                            className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60"
                                                            data-testid={`custom-product-row-${row.id}`}
                                                        >
                                                            {isEditing ? (
                                                                <div className="flex-1 space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <Input
                                                                            value={editingCustomName}
                                                                            onChange={(e) => setEditingCustomName(e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === "Enter") { e.preventDefault(); saveEditCustom(row.id); }
                                                                                if (e.key === "Escape") { e.preventDefault(); cancelEditCustom(); }
                                                                            }}
                                                                            className="flex-1 h-8 text-sm"
                                                                            autoFocus
                                                                            data-testid={`custom-product-edit-name-${row.id}`}
                                                                        />
                                                                        <Select value={editingCustomKind} onValueChange={setEditingCustomKind}>
                                                                            <SelectTrigger className="w-[110px] h-8 text-xs" data-testid={`custom-product-edit-kind-${row.id}`}>
                                                                                <SelectValue />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                <SelectItem value="product">Product</SelectItem>
                                                                                <SelectItem value="service">Service</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => saveEditCustom(row.id)}
                                                                            className="p-1 text-emerald-700 hover:text-emerald-900 rounded hover:bg-emerald-50"
                                                                            aria-label="Save"
                                                                            data-testid={`custom-product-edit-save-${row.id}`}
                                                                        >
                                                                            <Check size={14} weight="bold" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={cancelEditCustom}
                                                                            className="p-1 text-slate-500 hover:text-slate-800 rounded hover:bg-slate-100"
                                                                            aria-label="Cancel"
                                                                            data-testid={`custom-product-edit-cancel-${row.id}`}
                                                                        >
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                    <Textarea
                                                                        value={editingCustomDesc}
                                                                        onChange={(e) => setEditingCustomDesc(e.target.value)}
                                                                        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); cancelEditCustom(); } }}
                                                                        maxLength={2000}
                                                                        rows={5}
                                                                        placeholder="About this product (optional) — shown on the proposal under the product name"
                                                                        className="text-sm resize-y min-h-[110px]"
                                                                        data-testid={`custom-product-edit-desc-${row.id}`}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <Check size={12} weight="bold" className="text-emerald-600 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="block text-sm text-slate-800 truncate" data-testid={`custom-product-row-name-${row.id}`}>
                                                                            {row.productName}
                                                                        </span>
                                                                        {row.description ? (
                                                                            <span className="block text-[11px] text-slate-500 truncate" data-testid={`custom-product-row-desc-${row.id}`}>
                                                                                {row.description}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <Badge variant="outline" className="text-[10px] capitalize" data-testid={`custom-product-row-kind-${row.id}`}>
                                                                        {row.kind || "service"}
                                                                    </Badge>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => startEditCustom(row)}
                                                                        className="p-1 text-slate-500 hover:text-indigo-700 rounded hover:bg-indigo-50"
                                                                        aria-label="Edit"
                                                                        data-testid={`custom-product-edit-${row.id}`}
                                                                    >
                                                                        <Pencil size={14} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeFromScope(row.id)}
                                                                        className="p-1 text-slate-500 hover:text-red-600 rounded hover:bg-red-50"
                                                                        aria-label="Remove"
                                                                        data-testid={`custom-product-remove-${row.id}`}
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT — sticky summary panel */}
                    <aside className="lg:sticky lg:top-24" data-testid="product-selection-summary">
                        <div className="border border-slate-200 rounded-md bg-white shadow-sm overflow-hidden">
                            <div className="bg-slate-900 text-white px-3 py-2.5 flex items-center justify-between">
                                <span className="text-sm font-semibold">Selected Products</span>
                                <Badge className="bg-white text-slate-900 hover:bg-white" data-testid="summary-total-count">
                                    {summary.count}
                                </Badge>
                            </div>

                            <div className="px-3 py-2 max-h-72 overflow-y-auto" data-testid="summary-selected-list">
                                {summary.count === 0 ? (
                                    <div className="text-xs text-slate-500 italic py-2">No products selected yet.</div>
                                ) : (
                                    <ul className="space-y-1">
                                        {(scope || []).map((s) => (
                                            <li
                                                key={s.id}
                                                className="flex items-start gap-2 text-xs"
                                                data-testid={`summary-item-${s.id}`}
                                            >
                                                <span className="text-slate-400 leading-tight">•</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="truncate text-slate-800">{s.productName}</div>
                                                    {s.subtitle && (
                                                        <div className="truncate text-[10px] text-slate-500">{s.subtitle}</div>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFromScope(s.id)}
                                                    className="text-slate-400 hover:text-red-600"
                                                    aria-label="Remove"
                                                    data-testid={`summary-remove-${s.id}`}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5 text-xs">
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-600">One-Time Charges</span>
                                    <span className="font-semibold text-slate-900" data-testid="summary-one-time">
                                        {formatMoney(summary.oneTime, summary.cur)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-600">Annual Charges</span>
                                    <span className="font-semibold text-slate-900" data-testid="summary-annual">
                                        {formatMoney(summary.annual, summary.cur)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center pl-3 text-slate-500">
                                    <span>↳ AMC</span>
                                    <span data-testid="summary-amc">{formatMoney(summary.amc, summary.cur)}</span>
                                </div>
                                <div className="flex justify-between items-center pl-3 text-slate-500">
                                    <span>↳ Hosting</span>
                                    <span data-testid="summary-hosting">{formatMoney(summary.hosting, summary.cur)}</span>
                                </div>
                            </div>
                            <div className="px-3 py-1.5 border-t border-slate-200 bg-slate-50/60 text-[10px] text-slate-500 italic">
                                Pricing is captured in Sections 2 &amp; 3.
                            </div>
                        </div>
                    </aside>
                </div>
            </CardContent>
        </Card>
    );
}
